import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  Calculator,
  Check,
  Clock3,
  Layers,
  Lock,
  Medal,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
  Award,
  Crown,
  Gem,
  Rocket,
  ChevronRight,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import { getThemeColor } from '../../config/appConfig';
import '../../styles/module-shared.css';
import './account-tier.css';
import { formatDate, formatMoney } from './tierHelpers';
import { useAccountTierProgress } from './useAccountTier';
import PointsCalculatorModal from './PointsCalculatorModal';

const THEME = getThemeColor('primary');

const resolveTierIcon = (name = '') => {
  const n = String(name).toLowerCase();
  if (n.includes('pip') || n.includes('starter')) return Star;
  if (n.includes('market') || n.includes('booster')) return Zap;
  if (n.includes('bronze')) return Medal;
  if (n.includes('silver')) return Award;
  if (n.includes('gold')) return Crown;
  if (n.includes('platinum') || n.includes('vip')) return Gem;
  if (n.includes('pro') || n.includes('elite')) return Rocket;
  return Layers;
};

const ProgressRing = ({ current, target, percent, size = 148 }) => {
  const t = Number(target) || 0;
  const c = Number(current) || 0;
  const pct =
    percent != null && percent !== ''
      ? Math.min(100, Math.max(0, Math.round(Number(percent) || 0)))
      : t > 0
        ? Math.min(100, Math.round((c / t) * 100))
        : 0;
  const r = 42;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;

  return (
    <div className="at-mp-ring" style={{ width: size, height: size }} aria-label={`${c} of ${t} points`}>
      <svg viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#f1f5f9" strokeWidth="7" />
        <circle
          cx="50"
          cy="50"
          r={r}
          fill="none"
          stroke={THEME}
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="at-mp-ring__center">
        <strong>{pct}%</strong>
        <span>
          {c}/{t || 0}
        </span>
      </div>
    </div>
  );
};

const AccountTierProgress = () => {
  const { loading, error, userTier, currentTier, nextTier, tiers, history } =
    useAccountTierProgress('at-my-progress');
  const [calcOpen, setCalcOpen] = useState(false);

  if (loading) {
    return (
      <div className="page at-page">
        <div className="page-inner">
          <div className="module-card">
            <AppLoader color={THEME} />
          </div>
        </div>
      </div>
    );
  }

  const isMaxTier = Boolean(userTier?.isMaxTier);
  const tierName = userTier?.currentLevelName || currentTier?.name || '—';
  const rate = userTier?.maximumCommission ?? currentTier?.commissionPerLot ?? 0;
  const current = userTier?.currentPoints || 0;
  const target = userTier?.nextLevelPoints || userTier?.targetPoints || nextTier?.points || 0;
  const toNext =
    userTier?.pointsRemaining != null
      ? Math.max(0, userTier.pointsRemaining)
      : userTier?.pointsToNext != null
        ? Math.max(0, userTier.pointsToNext)
        : Math.max(0, target - current);
  const pct =
    userTier?.progressPercent != null
      ? userTier.progressPercent
      : userTier?.progressPct != null
        ? userTier.progressPct
        : target > 0
          ? Math.min(100, Math.round((current / target) * 100))
          : 0;
  const nextTierName = isMaxTier ? '' : userTier?.nextLevelName || nextTier?.name || '';
  const hasNextTier = !isMaxTier && Boolean(nextTierName);
  const TierIcon = resolveTierIcon(tierName);
  const NextIcon = resolveTierIcon(nextTierName || 'next');

  const unlockedCount = tiers.filter((t) => t.isCompleted || t.isCurrent).length;

  const timeline = [
    {
      icon: CalendarDays,
      label: 'Start date',
      value: formatDate(userTier?.startDate || userTier?.accountCreatedDate || userTier?.createdDate),
    },
    {
      icon: Clock3,
      label: 'Evaluation period',
      value: userTier?.evaluationPeriod || '—',
    },
    {
      icon: Sparkles,
      label: 'Last upgrade',
      value: formatDate(userTier?.lastUpgradeDate || userTier?.lastUpgrade),
    },
    {
      icon: CalendarDays,
      label: 'Account created',
      value: formatDate(userTier?.accountCreatedDate || userTier?.createdDate),
    },
  ];

  const metrics = [
    { icon: TrendingUp, label: 'Current points', value: current, tone: 'red' },
    { icon: Target, label: 'Next target', value: target || '—', tone: 'slate' },
    { icon: Users, label: 'Active clients', value: userTier?.activeClients ?? 0, tone: 'teal' },
    { icon: Sparkles, label: 'Points remaining', value: hasNextTier ? toNext : 0, tone: 'amber' },
    {
      icon: Clock3,
      label: 'Days left',
      value: userTier?.daysLeft == null ? '—' : userTier.daysLeft,
      tone: 'slate',
    },
    {
      icon: Layers,
      label: 'Tiers available',
      value: userTier?.tiersAvailable ?? tiers.length,
      tone: 'red',
    },
  ];

  return (
    <div className="page at-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={Medal}
          title="My Progress"
          subtitle="Track your account tier status and evaluation progress"
          iconBg={`linear-gradient(135deg,${THEME},#a52a2a)`}
        />
        <ModuleAlert message={error} />

        <section className="at-mp-banner">
          <div className="at-mp-banner__glow" aria-hidden="true" />
          <div className="at-mp-banner__main">
            <div className="at-mp-banner__identity">
              <div className="at-mp-banner__icon">
                <TierIcon className="w-7 h-7" />
              </div>
              <div>
                <div className="at-mp-banner__kicker">
                  <span className="at-mp-live" />
                  Active tier
                </div>
                <h2 className="at-mp-banner__title">{tierName}</h2>
                <p className="at-mp-banner__sub">
                  {tiers.length === 0
                    ? 'No partner tiers available yet.'
                    : isMaxTier || !hasNextTier
                      ? 'You are on the highest available partner tier.'
                      : `Climb toward ${nextTierName} to unlock a higher per-lot rate.`}
                </p>
              </div>
            </div>

            <div className="at-mp-banner__earn">
              <span>Earning up to</span>
              <strong>
                {formatMoney(rate)}
                <em>per lot</em>
              </strong>
            </div>
          </div>

          <div className="at-mp-banner__actions">
            <button type="button" className="at-mp-btn at-mp-btn--solid" onClick={() => setCalcOpen(true)}>
              <Calculator className="w-4 h-4" />
              Open calculator
            </button>
            <Link className="at-mp-btn at-mp-btn--ghost" to="/account-tier/how-it-works">
              How it works
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Progress tracker */}
        <section className="at-ov2-tracker">
          <div className="at-ov2-section-head">
            <h3>Progress tracker</h3>
            <span>
              {unlockedCount} / {Math.max(tiers.length, 1)} unlocked
            </span>
          </div>

          {tiers.length === 0 ? (
            <p className="at-empty">No partner tiers available yet.</p>
          ) : (
            <div className="at-ov2-track">
              {tiers.map((tier, index) => {
                const isCurrent = Boolean(tier.isCurrent);
                const isDone = Boolean(tier.isCompleted);
                const isLocked = !isDone && !isCurrent;
                const Icon = resolveTierIcon(tier.name);
                const showBridge = index < tiers.length - 1;
                const bridgePct = isDone ? 100 : isCurrent ? pct : 0;

                return (
                  <React.Fragment key={tier.id}>
                    <div
                      className={`at-ov2-node${isCurrent ? ' is-current' : ''}${
                        isDone ? ' is-done' : ''
                      }${isLocked ? ' is-locked' : ''}`}
                    >
                      <div className="at-ov2-node__icon">
                        {isDone ? (
                          <Check className="w-4 h-4" />
                        ) : isLocked ? (
                          <Lock className="w-3.5 h-3.5" />
                        ) : (
                          <Icon className="w-4 h-4" />
                        )}
                      </div>
                      <strong title={tier.name}>{tier.name}</strong>
                      <span>
                        {isCurrent
                          ? `${current}/${tier.levelPoints || tier.points || target || 0} pts`
                          : isDone
                            ? 'Completed'
                            : tier.points > 0
                              ? `${tier.points} pts`
                              : 'Locked'}
                      </span>
                    </div>
                    {showBridge ? (
                      <div className="at-ov2-bridge" aria-hidden="true">
                        <span style={{ width: `${bridgePct}%` }} />
                        {isCurrent && hasNextTier ? <em>{pct}%</em> : null}
                      </div>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </section>

        <section className="at-mp-journey">
          <div className="at-mp-journey__visual">
            <ProgressRing current={current} target={target} percent={pct} />
            <div className="at-mp-journey__pct-label">{pct}% complete</div>
          </div>

          <div className="at-mp-journey__body">
            <div className="at-mp-journey__head">
              <h3>Progress to next tier</h3>
              <p>
                {tiers.length === 0
                  ? 'No partner tiers available yet.'
                  : isMaxTier || !hasNextTier
                    ? 'Max tier reached — keep collecting points for activity tracking'
                    : `${toNext.toLocaleString('en-US')} points remaining to unlock ${nextTierName}`}
              </p>
            </div>

            <div className="at-mp-path">
              <div className="at-mp-path__node is-active">
                <div className="at-mp-path__icon">
                  <TierIcon className="w-4 h-4" />
                </div>
                <div>
                  <span>Now</span>
                  <strong>{tierName}</strong>
                </div>
              </div>
              <div className="at-mp-path__bridge" aria-hidden="true">
                <span style={{ width: `${pct}%` }} />
                <ChevronRight className="w-4 h-4" />
              </div>
              <div className={`at-mp-path__node${hasNextTier ? ' is-next' : ' is-max'}`}>
                <div className="at-mp-path__icon">
                  {hasNextTier ? <NextIcon className="w-4 h-4" /> : <Crown className="w-4 h-4" />}
                </div>
                <div>
                  <span>{hasNextTier ? 'Next' : 'Status'}</span>
                  <strong>{hasNextTier ? nextTierName : 'Max tier'}</strong>
                </div>
              </div>
            </div>

            <div className="at-mp-bar">
              <div className="at-mp-bar__track">
                <span style={{ width: `${pct}%` }} />
              </div>
              <div className="at-mp-bar__labels">
                <span>{current.toLocaleString('en-US')} pts</span>
                <span>{(target || 0).toLocaleString('en-US')} pts</span>
              </div>
            </div>

            <div className="at-mp-journey__meta">
              <div>
                <Target className="w-3.5 h-3.5" />
                Target {target || '—'}
              </div>
              <div>
                <Clock3 className="w-3.5 h-3.5" />
                Days left {userTier?.daysLeft == null ? '—' : userTier.daysLeft}
              </div>
              <div>
                <Layers className="w-3.5 h-3.5" />
                {userTier?.tiersAvailable ?? tiers.length} tiers
              </div>
            </div>
          </div>
        </section>

        <section className="at-mp-metrics">
          <div className="at-mp-section-head">
            <h3>Activity snapshot</h3>
            <Link to="/account-tier/how-it-works" className="at-mp-section-link">
              How it works <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="at-mp-metrics__grid">
            {metrics.map((m) => {
              const Icon = m.icon;
              return (
                <article key={m.label} className={`at-mp-metric is-${m.tone}`}>
                  <div className="at-mp-metric__icon">
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <span>{m.label}</span>
                    <strong>{m.value}</strong>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="at-mp-timeline">
          <div className="at-mp-section-head">
            <h3>Account timeline</h3>
          </div>
          <div className="at-mp-timeline__grid">
            {timeline.map((row) => {
              const Icon = row.icon;
              return (
                <article key={row.label} className="at-mp-time-card">
                  <div className="at-mp-time-card__icon">
                    <Icon className="w-4 h-4" />
                  </div>
                  <span>{row.label}</span>
                  <strong>{row.value}</strong>
                </article>
              );
            })}
          </div>
        </section>

        {/* Upgrade history */}
        <section className="at-ov2-history">
          <div className="at-ov2-section-head">
            <h3>Evaluation History</h3>
            <span>
              {history.length} record{history.length === 1 ? '' : 's'}
            </span>
          </div>

          {history.length === 0 ? (
            <p className="at-empty">No tier history yet.</p>
          ) : (
            <div className="at-ov2-timeline">
              {history.map((row, index) => (
                <div key={row.id} className="at-ov2-timeline__item">
                  <div className="at-ov2-timeline__rail">
                    <span className="at-ov2-timeline__dot">
                      <Check className="w-3 h-3" />
                    </span>
                    {index < history.length - 1 ? (
                      <span className="at-ov2-timeline__line" />
                    ) : null}
                  </div>
                  <div className="at-ov2-timeline__content">
                    <time>{row.startDate}</time>
                    <p>{row.message || '—'}</p>
                    {row.levelName ? <strong>{row.levelName}</strong> : null}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      <PointsCalculatorModal open={calcOpen} onClose={() => setCalcOpen(false)} />
    </div>
  );
};

export default AccountTierProgress;
